import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";

export const Route = createFileRoute("/auth")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    next: typeof s.next === "string" ? s.next : "",
  }),
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Sign in | Avolus" },
      { name: "description", content: "Sign in to your Avolus account to connect MCP clients and manage your rentals." },
      { property: "og:title", content: "Sign in | Avolus" },
      { property: "og:description", content: "Sign in to your Avolus account." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

function safeNext(next: string): string {
  if (!next.startsWith("/") || next.startsWith("//")) return "/";
  return next;
}

function AuthPage() {
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const target = safeNext(next);

  // Bounce already-signed-in users to their destination.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!cancelled && data.session) window.location.replace(target);
    })();
    return () => {
      cancelled = true;
    };
  }, [target]);

  async function onEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    if (mode === "signin") {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setBusy(false);
        setError(error.message);
        return;
      }
      window.location.replace(target);
    } else {
      const emailRedirectTo = `${window.location.origin}/auth${next ? `?next=${encodeURIComponent(target)}` : ""}`;
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo },
      });
      setBusy(false);
      if (error) {
        setError(error.message);
        return;
      }
      setInfo("Check your inbox to confirm your email, then sign in.");
      setMode("signin");
    }
  }

  async function onGoogle() {
    setBusy(true);
    setError(null);
    // Preserve the destination through the OAuth round-trip.
    try {
      sessionStorage.setItem("avolus:postAuthRedirect", target);
    } catch {}
    const redirectPath = `/auth${next ? `?next=${encodeURIComponent(target)}` : ""}`;
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}${redirectPath}`,
    });
    if (result.error) {
      setBusy(false);
      setError(result.error.message ?? "Google sign-in failed");
      return;
    }
    if (result.redirected) return;
    // Session was set inline (popup/web_message path).
    const stored = (() => {
      try {
        return sessionStorage.getItem("avolus:postAuthRedirect");
      } catch {
        return null;
      }
    })();
    window.location.replace(stored || target);
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-24 bg-background text-foreground">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {mode === "signin" ? "Sign in to Avolus" : "Create your Avolus account"}
          </h1>
          <p className="text-sm text-muted-foreground">
            Required to connect Avolus MCP tools to an AI assistant.
          </p>
        </div>

        <button
          type="button"
          onClick={onGoogle}
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2.5 text-sm font-medium hover:bg-accent transition-colors disabled:opacity-60"
        >
          Continue with Google
        </button>

        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
          <div className="h-px flex-1 bg-border" />
          or
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={onEmailSubmit} className="space-y-3">
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Password</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </label>

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {info && <p className="text-sm text-muted-foreground">{info}</p>}

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          {mode === "signin" ? (
            <>
              No account?{" "}
              <button
                type="button"
                className="underline hover:text-foreground"
                onClick={() => {
                  setMode("signup");
                  setError(null);
                  setInfo(null);
                }}
              >
                Create one
              </button>
            </>
          ) : (
            <>
              Already have one?{" "}
              <button
                type="button"
                className="underline hover:text-foreground"
                onClick={() => {
                  setMode("signin");
                  setError(null);
                  setInfo(null);
                }}
              >
                Sign in
              </button>
            </>
          )}
        </p>

        <button
          type="button"
          onClick={() => navigate({ to: "/" })}
          className="w-full text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
        >
          ← Back to home
        </button>
      </div>
    </main>
  );
}
